import { gql, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import { useVariables } from 'pared/Routes/renderer/variables'
import { getDateRangeLabelV2 } from 'pared/utils/date'

import { useDateFilter } from '../../../dateFilter'
import { useGroupFilter } from '../../../groupFilter'
import { IApiDataType } from '../../types'

interface IDataType {
  trendLocationPurchase: {
    nodes: {
      startDate: string
      endDate: string
      businessYear: string
      businessQuarter: string
      businessMonth: string
      businessWeekOfMonth: string
      businessWeek: string
      netSales: number
      itemDetails: Record<
        string,
        {
          type: 'all' | 'item' | 'category'
          item: string
          actualUsage: number
          actualUsagePercent: number
          end: number
          percent: number
        }
      >
    }[]
  }
}

const query = gql`
  query trendLocationPurchase(
    $iLocationId: Int!
    $iEndDate: Date!
    $iGroupBy: String!
    $iDateRangeNumber: Int!
  ) {
    trendLocationPurchase(
      iLocationId: $iLocationId
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iDateRangeNumber: $iDateRangeNumber
    ) {
      nodes {
        startDate
        endDate
        businessYear
        businessQuarter
        businessMonth
        businessWeekOfMonth
        businessWeek
        netSales
        itemDetails
      }
    }
  }
`

export const bbbTrendLocationPurchaseConfigs = {
  date: 'string',
  percentOfNetSales: 'percent',
} as const

const useBbbTrendLocationPurchase = () => {
  const { variables } = useVariables()
  const { startDate, endDate } = useDateFilter()
  const { groupFilter } = useGroupFilter()

  const { data, loading } = useQuery<IDataType>(query, {
    variables: {
      iLocationId: groupFilter?.ids[0],
      iEndDate: endDate,
      iGroupBy: 'last_x_weeks',
      iDateRangeNumber: 8,
    },
    skip: !startDate || !endDate || !groupFilter,
  })
  const category = variables.bbbTrendPurchaseCategories?.value[0]?.slice(-1)[0]
  const categoryIsLoading = !category

  return {
    data: useMemo((): IApiDataType => {
      const trendData = data?.trendLocationPurchase.nodes

      if (!trendData || !category) return null

      return trendData.map((d) => {
        const value = d.itemDetails[category]

        return {
          ...value,
          date:
            'startDate' in d
              ? getDateRangeLabelV2('business_week', {
                  ...d,
                  groupByStartDate: d.startDate,
                  groupByEndDate: d.endDate,
                })
              : null,
          percentOfNetSales: value?.percent,
        }
      })
    }, [data, category]),
    loading: loading || categoryIsLoading,
  }
}

export default useBbbTrendLocationPurchase
